/**
 * ENG-6153 — detect a wedged-but-alive agent session.
 *
 * A Claude session can wedge while its process stays alive: tmux up, claude
 * running, but the turn stops progressing (hung Stop hook / stuck render) and
 * inbound channel messages stop draining. `isSessionHealthy` only checks
 * tmux+process liveness, so it reads such a session as healthy and never
 * respawns it — and a manual restart `--resume`s the same transcript and
 * re-wedges (the sherlock incident, 2026-06-08). This module decides, from the
 * cheap per-agent signals the responsiveness probe already collects, whether a
 * session is wedged and should be respawned FRESH (rotated UUID, no --resume).
 *
 * Pure + injectable (no I/O) so the predicate is unit-testable, mirroring
 * `decideRestartGate` in update-window-gate.ts. The poll loop owns the
 * per-agent consecutive-cycle counter and the actual respawn.
 *
 * Signal design (see ENG-6153 plan):
 *   - PRIMARY = undrained inbound. `pendingInboundOldestAgeSeconds` is the age
 *     of the oldest `*-pending-inbound` marker (ENG-6017). A healthy session
 *     drains inbound within seconds, so a marker older than the threshold means
 *     the agent isn't consuming messages. `null` (no markers) ⇒ never wedged —
 *     a legitimately-idle low-traffic agent has an empty queue.
 *   - pane-age is used ONLY to tell a legit long turn from a wedge: while the
 *     pane is advancing (fresh mtime) the agent may be mid-turn with inbound
 *     queued behind it, so we defer — UNTIL inbound age crosses the higher
 *     hard-wait, at which point we treat it as wedged regardless of the pane.
 *     (An *animated* frozen spinner keeps pane.log fresh, so pane-stale alone
 *     would miss exactly the failure we're targeting.)
 */

export type WedgeRestartMode = 'off' | 'shadow' | 'enforce';

export interface WedgeConfig {
  mode: WedgeRestartMode;
  /** Inbound marker age (s) past which a stale pane counts as a wedge candidate. */
  inboundWaitSeconds: number;
  /**
   * ENG-6264: opt-in absolute backstop. Inbound marker age (s) past which an
   * actively-PRODUCING session is force-respawned anyway (a producing-but-never-
   * draining runaway loop). `0` (the default) DISABLES it — a working agent is
   * never respawned. A frozen/hung session (not producing) is reaped by the soft
   * path regardless of this value.
   */
  inboundHardWaitSeconds: number;
  /** Pane.log age (s) at/over which the pane is considered NOT advancing. */
  paneStaleSeconds: number;
  /**
   * ENG-6238: transcript JSONL age (s) at/over which the session is considered
   * NOT actively producing tokens. The session transcript grows as the model
   * streams turns/tool calls, so a fresh transcript is the reliable "real work
   * is happening" signal — unlike pane.log, which an *animated frozen spinner*
   * keeps fresh without producing anything.
   */
  transcriptStaleSeconds: number;
  /** Consecutive qualifying poll cycles required before acting (hysteresis). */
  minCycles: number;
}

export interface WedgeSignals {
  /** Age (s) of the agent's pane.log, or null when it doesn't exist. */
  paneActivityAgeSeconds: number | null;
  /**
   * ENG-6238: age (s) of the current session's transcript JSONL, or null when
   * it can't be resolved/stat'd. The primary "is the model actually producing
   * tokens" signal; pane age is the fallback when this is null.
   */
  transcriptActivityAgeSeconds: number | null;
  /**
   * ENG-6294: age (s) of the freshest sub-agent transcript under
   * `<transcriptDir>/<sessionId>/subagents/`, or null when there is none.
   * The ENG-6274 background-dispatch flow makes a HEALTHY parent match the
   * wedge signature exactly — it acks, dispatches a `run_in_background`
   * worker, and ends its turn, so the pane goes quiet and the main transcript
   * stops growing while inbound stays queued. The worker's own jsonl keeps
   * growing the whole time, so a fresh mtime here counts as producing.
   */
  subagentActivityAgeSeconds: number | null;
  /** Age (s) of the oldest undrained pending-inbound marker, or null when none. */
  pendingInboundOldestAgeSeconds: number | null;
}

const DEFAULTS = {
  inboundWaitSeconds: 120,
  // ENG-6264: DISABLED by default (0). A session that's actively producing
  // tokens is never force-respawned — a working agent must not be killed just
  // because a message has been queued behind its turn, no matter how long.
  // ENG-6238 made this an absolute backstop (1200s) to still catch a
  // producing-but-never-draining runaway loop, but that re-introduced the exact
  // failure we set out to kill: cutting off real work on a long turn. Runaway
  // token burn is owned by the cost guardrail (ENG-5556); a producing-but-silent
  // loop still trips the synthetic-probe alarm. So the backstop is now opt-in:
  // set AGT_WEDGE_INBOUND_HARD_WAIT_SECONDS to a positive value to re-enable it
  // (floored at inboundWaitSeconds). 0 = the frozen/hung wedge (transcript
  // static) is still caught by the soft path; only the *producing* path is spared.
  inboundHardWaitSeconds: 0,
  paneStaleSeconds: 120,
  // ENG-8196 INTERIM MITIGATION (was 60). A Claude Code transcript gets one entry
  // per message, so a single long tool call — a page rebuild, a web fetch, an image
  // generation, a slow MCP round-trip — writes a `tool_use` entry and then NOTHING
  // until the `tool_result` returns. Any tool call longer than this threshold looks
  // identical to a frozen turn, and the detector force-respawned actively-producing
  // sessions on it (jarvis: transcriptAge=113s while paneAge=0s; don: 202s mid-build).
  // 900s buys headroom for realistic long tool calls. It is NOT the fix: it only
  // widens the window, so a sufficiently long tool call still dies and genuine wedge
  // detection is delayed (worst case ~45min at minCycles=3, up from ~3min). The real
  // fix is ENG-6238's unimplemented turn-boundary "sharp signal" — has the agent
  // completed any turn since the inbound queued? — which needs a new WedgeSignals
  // field and stays open on ENG-8196.
  // BLAST RADIUS, deliberate: `isSessionProducing` reuses this one knob for
  // `subagentActivityAgeSeconds` too, so the ENG-6294 background-task exemption
  // ("a subagent wrote recently ⇒ producing") widens 60s → 900s along with the main
  // transcript window. That is accepted, not overlooked — ENG-6294 documents the
  // exemption as self-bounding precisely BECAUSE it lapses at this threshold, so
  // splitting it onto its own knob would decouple the two windows that comment
  // relies on being identical. Revisit when the turn-boundary signal lands.
  // Per-host override: AGT_WEDGE_TRANSCRIPT_STALE_SECONDS (takes precedence).
  transcriptStaleSeconds: 900,
  minCycles: 3,
} as const;

function parseMode(raw: string | undefined): WedgeRestartMode {
  const v = (raw ?? '').trim().toLowerCase();
  // ENG-7195: default to `shadow` (log-only, no restarts) when unset so wedge
  // detections — including the wedged-but-socket-connected case that swallows a
  // Slack /restart — are observable fleet-wide. Explicit `off` still disables;
  // `enforce` opts into auto-restart once shadow has been validated.
  if (v === 'shadow' || v === 'enforce' || v === 'off') return v;
  return 'shadow';
}

function parsePositiveInt(raw: string | undefined, fallback: number, floor: number): number {
  const n = raw ? Number.parseInt(raw, 10) : NaN;
  return Number.isInteger(n) && n >= floor ? n : fallback;
}

/**
 * Resolve the wedge-detection config from the environment. Mode defaults to
 * `shadow` (log-only) — set AGT_WEDGE_RESTART_MODE=enforce to auto-restart
 * wedged sessions, or `off` to disable detection entirely (ENG-7195).
 * `inboundHardWaitSeconds` defaults to 0 = DISABLED (ENG-6264: never respawn a
 * producing agent); a positive override re-enables the backstop, floored at
 * `inboundWaitSeconds` so the hard gate can never be looser than the soft one.
 */
export function resolveWedgeConfig(env: NodeJS.ProcessEnv = process.env): WedgeConfig {
  const inboundWaitSeconds = parsePositiveInt(
    env.AGT_WEDGE_INBOUND_WAIT_SECONDS,
    DEFAULTS.inboundWaitSeconds,
    30,
  );
  // ENG-6264: 0 (default) disables the producing-session backstop entirely. Only
  // a positive value re-enables it, and then it's floored at the soft wait.
  const inboundHardWaitRaw = parsePositiveInt(
    env.AGT_WEDGE_INBOUND_HARD_WAIT_SECONDS,
    DEFAULTS.inboundHardWaitSeconds,
    0,
  );
  const inboundHardWaitSeconds = inboundHardWaitRaw <= 0 ? 0 : Math.max(inboundWaitSeconds, inboundHardWaitRaw);
  return {
    mode: parseMode(env.AGT_WEDGE_RESTART_MODE),
    inboundWaitSeconds,
    inboundHardWaitSeconds,
    paneStaleSeconds: parsePositiveInt(env.AGT_WEDGE_PANE_STALE_SECONDS, DEFAULTS.paneStaleSeconds, 30),
    transcriptStaleSeconds: parsePositiveInt(
      env.AGT_WEDGE_TRANSCRIPT_STALE_SECONDS,
      DEFAULTS.transcriptStaleSeconds,
      15,
    ),
    minCycles: parsePositiveInt(env.AGT_WEDGE_MIN_CYCLES, DEFAULTS.minCycles, 2),
  };
}

/**
 * ENG-6238: is the session actively PRODUCING output this cycle?
 *
 * Primary signal: the transcript JSONL is growing (`transcriptActivityAgeSeconds`
 * under the stale threshold). A hung/frozen turn — the sherlock wedge — leaves
 * the transcript static even while the spinner animates and keeps pane.log
 * fresh, so this is the discriminator pane-age could never be.
 *
 * Fallback: when the transcript age can't be resolved (null — e.g. a path-
 * encoding miss), fall back to pane age so behaviour degrades to the pre-6238
 * heuristic rather than treating an unknown as "frozen".
 *
 * ENG-6294: a fresh SUB-AGENT transcript also counts as producing — a parent
 * that dispatched a `run_in_background` worker and ended its turn is healthy,
 * not wedged, while the worker's jsonl keeps growing. The exemption is
 * self-bounding: the moment the worker stops writing (done OR itself hung),
 * its mtime crosses `transcriptStaleSeconds` and the exemption lapses; and
 * because it routes through the producing path, the opt-in
 * `inboundHardWaitSeconds` backstop still caps an ever-producing session.
 */
export function isSessionProducing(signals: WedgeSignals, config: WedgeConfig): boolean {
  const subagentAge = signals.subagentActivityAgeSeconds;
  if (subagentAge !== null && subagentAge < config.transcriptStaleSeconds) return true;
  const transcriptAge = signals.transcriptActivityAgeSeconds;
  if (transcriptAge !== null) return transcriptAge < config.transcriptStaleSeconds;
  const paneAge = signals.paneActivityAgeSeconds;
  return paneAge !== null && paneAge < config.paneStaleSeconds;
}

/**
 * ENG-6294: why is this cycle NOT a wedge candidate, when the only thing
 * sparing it is the background sub-agent signal? Returns
 * `'background-task-in-flight'` when the session (a) has inbound queued past
 * the soft wait, and (b) would be a wedge-candidate cycle if the sub-agent
 * signal were removed, but (c) is not one with it present. The manager logs
 * this so a suppressed wedge episode is visible in manager.log instead of
 * silently resetting the streak. Returns null in every other case.
 */
export function wedgeExemptionReason(
  signals: WedgeSignals,
  config: WedgeConfig,
): 'background-task-in-flight' | null {
  if (signals.subagentActivityAgeSeconds === null) return null;
  if (isWedgeCandidateCycle(signals, config)) return null; // not exempt — it IS a candidate
  const withoutSubagent: WedgeSignals = { ...signals, subagentActivityAgeSeconds: null };
  return isWedgeCandidateCycle(withoutSubagent, config) ? 'background-task-in-flight' : null;
}

/**
 * Does THIS poll cycle look like a wedge? (Used by the caller to advance or
 * reset the per-agent consecutive-cycle counter.)
 *
 * ENG-6238 — the verdict now turns on whether the session is *producing*
 * (see `isSessionProducing`), not on pane.log mtime:
 *   - No waiting inbound → idle, never a wedge.
 *   - Inbound under the soft wait → too soon, defer.
 *   - Producing (transcript advancing) → a legitimate long turn; defer until
 *     the inbound passes the absolute backstop (`inboundHardWaitSeconds`), which
 *     only catches a pathological still-producing loop. This is what spares
 *     kylie's 8-min turns that the old pane-blind hard cap force-killed.
 *   - Not producing (frozen/hung turn) with inbound past the soft wait → wedge,
 *     even while an animated spinner keeps pane.log fresh (the sherlock case).
 */
export function isWedgeCandidateCycle(signals: WedgeSignals, config: WedgeConfig): boolean {
  const inboundAge = signals.pendingInboundOldestAgeSeconds;
  if (inboundAge === null) return false; // empty queue → idle, never a wedge
  if (inboundAge < config.inboundWaitSeconds) return false;
  if (isSessionProducing(signals, config)) {
    // ENG-6264: a session that's actively producing tokens is doing real work —
    // never force-respawn it just because a message is queued behind its turn.
    // The absolute backstop is opt-in (inboundHardWaitSeconds > 0) for a
    // producing-but-never-draining runaway loop; disabled (0, the default) means
    // a working agent is spared indefinitely. The frozen/hung wedge (transcript
    // static) is unaffected — it's caught by the not-producing path below.
    if (config.inboundHardWaitSeconds <= 0) return false;
    return inboundAge >= config.inboundHardWaitSeconds;
  }
  // Not producing: a frozen/hung turn that's stopped draining inbound.
  return true;
}

/**
 * Decide whether to force-fresh respawn. `'wedged'` only when this cycle is a
 * candidate AND the candidate streak has reached `minCycles` (hysteresis — a
 * single observation never acts).
 */
export function decideWedgeRestart(
  input: WedgeSignals & { consecutiveWedgeCycles: number },
  config: WedgeConfig,
): 'none' | 'wedged' {
  if (!isWedgeCandidateCycle(input, config)) return 'none';
  return input.consecutiveWedgeCycles >= config.minCycles ? 'wedged' : 'none';
}

/**
 * CS-1680: should a HUMAN-INITIATED restart force a fresh session rather than
 * `--resume` the existing transcript?
 *
 * The restart lane kills the claude process but leaves the daily-session UUID
 * pin untouched, so `resolveSessionSpawnDecision` finds today's transcript and
 * resumes it — replaying the very transcript that wedged the agent. A Zanda
 * admin restarted `alfred` twice, three hours apart, and watched it resume the
 * same card and re-wedge both times.
 *
 * Note the automatic path's rotation gate could not have covered this: it counts
 * `consecutiveSameUuidFailures`, which increments only on SPAWN FAILURE. A wedge
 * spawns fine and *then* freezes, so it never increments — which is why both of
 * alfred's restarts logged `restart #0`.
 *
 * Deliberately narrow. It answers only "does the detector currently read this
 * agent as wedged", using the streak the detector already maintains. That means:
 *
 *   - It reads the shadow signal without auto-restarting anything, so it does
 *     not pre-empt ENG-7195's soak. Shadow keeps the streak (it `continue`s
 *     without clearing), so the signal is available in every mode but `off`.
 *   - An ordinary restart of a healthy agent still resumes, so a routine restart
 *     does not silently discard a day's continuity — the tradeoff
 *     `prepareForRespawn` priced deliberately and chose to keep.
 *
 * `off` disables detection entirely, so it must also disable this: with no
 * detector running the streak is never updated and a stale value must not be
 * allowed to force a rotation.
 */
export function shouldForceFreshOnManualRestart(
  consecutiveWedgeCycles: number,
  config: WedgeConfig,
): boolean {
  if (config.mode === 'off') return false;
  return consecutiveWedgeCycles >= config.minCycles;
}
